import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, FileJson, Check, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const TEMPLATE = {
  title: "Knife Skills Basics",
  slug: "knife-skills-basics",
  category: "skills",
  difficulty: "beginner",
  readTime: "10 min",
  introduction: "Learn the fundamental knife techniques used by professional chefs.",
  sections: [
    {
      heading: "The Chef's Knife",
      content: "The most versatile knife in the kitchen. A good chef's knife is 8-10 inches long.",
      tips: ["Keep your knife sharp", "Use a stable cutting board"]
    }
  ],
  status: "published"
};

export const AdminImportTechniquesPage = () => {
  const [jsonInput, setJsonInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const navigate = useNavigate();

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return { Authorization: `Bearer ${token}` };
  };

  const handleImport = async () => {
    if (!jsonInput.trim()) { toast.error('Paste JSON first'); return; }
    setLoading(true);
    setResult(null);

    try {
      const parsed = JSON.parse(jsonInput);
      const isBulk = Array.isArray(parsed) || parsed.techniques;
      const endpoint = isBulk
        ? `${API_URL}/api/admin/techniques/import-json-bulk`
        : `${API_URL}/api/admin/techniques/import-json`;

      const body = isBulk
        ? { techniques: Array.isArray(parsed) ? parsed : parsed.techniques }
        : parsed;

      const res = await axios.post(endpoint, body, { headers: getAuthHeaders() });

      if (isBulk) {
        setResult({
          success: true,
          message: `Imported: ${res.data.imported_count}, Skipped: ${res.data.skipped_count}, Errors: ${res.data.error_count}`,
          details: res.data
        });
        toast.success(`Imported ${res.data.imported_count} technique(s)`);
      } else {
        setResult({ success: true, message: `Imported: ${res.data.slug}` });
        toast.success('Technique imported!');
      }
    } catch (error) {
      if (error instanceof SyntaxError) {
        setResult({ success: false, message: `Invalid JSON: ${error.message}` });
      } else {
        setResult({
          success: false,
          message: error.response?.data?.detail || 'Import failed'
        });
      }
      toast.error('Import failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background p-4" data-testid="techniques-import-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <Link to="/admin/techniques">
            <Button variant="outline" size="sm"><ArrowLeft className="w-4 h-4 mr-2" /> Techniques Manager</Button>
          </Link>
          <h1 className="text-2xl font-bold text-foreground">Import Techniques (JSON)</h1>
        </div>

        <Alert className="mb-4 border-amber-300 bg-amber-50/10">
          <AlertCircle className="h-4 w-4 text-amber-500" />
          <AlertDescription className="text-foreground">
            Imported techniques default to <strong>"draft"</strong> status. After importing, go to the{' '}
            <Link to="/admin/techniques" className="text-amber-500 underline font-medium">Techniques Manager</Link>{' '}
            and click <strong>"Publish All Drafts"</strong> to make them visible on the site.
            Or set <code>"status": "published"</code> in your JSON.
          </AlertDescription>
        </Alert>

        <Card className="bg-card border-border mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-foreground">
              <FileJson className="w-5 h-5" /> Paste Technique JSON
            </CardTitle>
            <CardDescription>
              Paste a single technique object or an array of techniques.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              placeholder='Paste technique JSON here...'
              value={jsonInput}
              onChange={(e) => { setJsonInput(e.target.value); setResult(null); }}
              className="font-mono text-sm min-h-[400px] bg-muted border-border text-foreground"
              data-testid="technique-json-input"
            />
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => { setJsonInput(JSON.stringify(TEMPLATE, null, 2)); setResult(null); }}
              >
                Load Template
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setJsonInput(JSON.stringify([TEMPLATE], null, 2));
                  setResult(null);
                }}
              >
                Load Bulk Template
              </Button>
              <Button onClick={handleImport} disabled={loading} className="bg-primary hover:bg-primary/90" data-testid="import-techniques-btn">
                {loading ? 'Importing...' : 'Import'}
              </Button>
            </div>

            {result && (
              <Alert variant={result.success ? 'default' : 'destructive'}>
                {result.success ? <Check className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
                <AlertDescription>
                  {result.message}
                  {result.success && (
                    <Link to="/admin/techniques" className="ml-2 text-amber-500 underline font-medium">
                      Go to Techniques Manager to publish
                    </Link>
                  )}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminImportTechniquesPage;
